import { create } from 'zustand';
import { Driver } from '../types';
import { 
  getDrivers, 
  getDriverById, 
  createDriver, 
  updateDriver, 
  deleteDriver 
} from '../api/supabase';
import { driverHybridStore } from '../services/offline';

interface DriverState {
  drivers: Driver[];
  selectedDriver: Driver | null;
  loading: boolean;
  error: string | null;
  
  fetchDrivers: () => Promise<void>;
  fetchDriverById: (id: string) => Promise<void>;
  addDriver: (driver: Omit<Driver, 'id' | 'created_at'>) => Promise<void>;
  editDriver: (id: string, driver: Partial<Driver>) => Promise<void>;
  removeDriver: (id: string) => Promise<void>;
  setSelectedDriver: (driver: Driver | null) => void;
  assignDriver: (driverId: string, vehicleId: string) => Promise<void>;
  unassignDriver: (driverId: string) => Promise<void>;
}

export const useDriverStore = create<DriverState>((set, get) => ({
  drivers: [],
  selectedDriver: null,
  loading: false,
  error: null,
  
  fetchDrivers: async () => {
    set({ loading: true, error: null });
    try {
      console.log('👨‍💼 DriverStore: Fetching drivers (hybrid)...');
      
      // Get drivers from hybrid storage (localStorage cache + SQLite backup)
      const drivers = await driverHybridStore.getAll();
      console.log('👨‍💼 DriverStore: Received drivers from hybrid storage:', drivers.length);
      
      // Set initial drivers (even if empty) to remove loading state
      set({ drivers, loading: false });
      
      // Try to refresh from server if online (background refresh)
      setTimeout(async () => {
        try {
          await driverHybridStore.refreshFromServer();
          
          // Get updated drivers after refresh
          const updatedDrivers = await driverHybridStore.getAll();
          console.log('👨‍💼 DriverStore: Updated drivers after server refresh:', updatedDrivers.length);
          
          // Update store with fresh data
          set({ drivers: updatedDrivers });
        } catch (syncError) {
          console.warn('⚠️ Background driver refresh failed:', syncError);
        }
      }, 1500); // Slightly delayed to avoid overlap with vehicle refresh
      
    } catch (error: any) {
      console.error('👨‍💼 DriverStore: Error fetching drivers:', error);
      set({ loading: false, error: error.message });
    }
  },
  
  fetchDriverById: async (id) => {
    set({ loading: true, error: null });
    try {
      const driver = await driverHybridStore.getById(id);
      set({ selectedDriver: driver, loading: false });
    } catch (error: any) {
      set({ loading: false, error: error.message });
    }
  },
  
  addDriver: async (driver) => {
    set({ loading: true, error: null });
    try {
      // Create driver using hybrid storage (offline-first)
      const driverData = {
        name: driver.name,
        phone: driver.phone,
        license_number: driver.licenseNumber,
        license_expiry: driver.licenseExpiry,
        image_url: driver.image_url,
        status: driver.status || 'inactive',
        current_vehicle_id: driver.currentVehicleId,
        daily_target: driver.dailyTarget,
        deposit: driver.deposit,
        nationality: driver.nationality,
        notes: driver.notes,
      };

      const newDriver = await driverHybridStore.create(driverData);
      console.log('✅ Driver created (hybrid):', newDriver);
      
      // Log activity after successful creation (only if saved to database, not localStorage)
      // Check if ID is a valid UUID (not a temporary localStorage ID like "drivers_123_abc")
      const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(newDriver.id);
      
      if (isValidUUID) {
        try {
          const { logActivity } = require('../api/activityLog');
          
          await logActivity({
            entity_type: 'driver',
            entity_id: newDriver.id,
            action_type: 'create',
            changes: {
              driver: { before: null, after: newDriver.name },
              phone: { before: null, after: newDriver.phone },
              status: { before: null, after: newDriver.status },
              license: { before: null, after: newDriver.license_number },
            },
            description: `Created new driver: ${newDriver.name} (${newDriver.phone})`,
          });
          console.log('✅ Driver creation logged to activity log');
        } catch (logError) {
          console.error('⚠️ Failed to log driver creation:', logError);
          // Don't fail the creation if logging fails
        }
      } else {
        console.log('⏭️  Skipping activity log for offline driver (will log when synced to database)');
      }
      
      // Convert to app format if needed
      const formattedDriver: Driver = {
        id: newDriver.id,
        name: newDriver.name,
        phone: newDriver.phone,
        licenseNumber: newDriver.license_number,
        licenseExpiry: newDriver.license_expiry,
        image_url: newDriver.image_url,
        status: newDriver.status as any,
        currentVehicleId: newDriver.current_vehicle_id,
        dailyTarget: newDriver.daily_target,
        deposit: newDriver.deposit,
        nationality: newDriver.nationality,
        notes: newDriver.notes,
        created_at: newDriver.created_at,
      };
      
      set({ 
        drivers: [...get().drivers, formattedDriver],
        selectedDriver: formattedDriver,
        loading: false 
      });
    } catch (error: any) {
      console.error('❌ Error creating driver (hybrid):', error);
      set({ loading: false, error: error.message });
    }
  },
  
  editDriver: async (id, driver) => {
    set({ loading: true, error: null });
    try {
      // Get current driver data for activity logging
      const currentDriver = get().drivers.find(d => d.id === id);
      
      // Convert from app schema to database schema
      const dbDriver: any = {};
      
      if (driver.name !== undefined) dbDriver.name = driver.name;
      if (driver.phone !== undefined) dbDriver.phone = driver.phone;
      if (driver.licenseNumber !== undefined) dbDriver.license_number = driver.licenseNumber;
      if (driver.licenseExpiry !== undefined) dbDriver.license_expiry = driver.licenseExpiry;
      if (driver.image_url !== undefined) dbDriver.image_url = driver.image_url;
      if (driver.status !== undefined) dbDriver.status = driver.status;
      if (driver.currentVehicleId !== undefined) dbDriver.current_vehicle_id = driver.currentVehicleId;
      if (driver.dailyTarget !== undefined) dbDriver.daily_target = driver.dailyTarget;
      if (driver.deposit !== undefined) dbDriver.deposit = driver.deposit;
      if (driver.nationality !== undefined) dbDriver.nationality = driver.nationality;
      if (driver.notes !== undefined) dbDriver.notes = driver.notes;
      
      console.log('📤 Updating driver DIRECTLY in Supabase (bypassing hybrid):', { id, fields: Object.keys(dbDriver) });
      
      // BYPASS HYBRID STORAGE - Go directly to Supabase
      const { supabase } = require('../api/supabase');
      const { data: updatedData, error: updateError } = await supabase
        .from('drivers')
        .update(dbDriver)
        .eq('id', id)
        .select()
        .single();
      
      if (updateError) {
        throw new Error(`Database update failed: ${updateError.message}`);
      }
      
      if (!updatedData) {
        throw new Error('No data returned from database update');
      }
      
      console.log('✅ Driver updated directly in database:', updatedData);
      
      // Log activity with before/after values
      try {
        const { logActivity } = require('../api/activityLog');
        
        const changes: Record<string, { before: any; after: any }> = {};
        const changedFields: string[] = [];
        
        if (currentDriver) {
          if (driver.name && currentDriver.name !== driver.name) {
            changes['Name'] = { before: currentDriver.name, after: driver.name };
            changedFields.push('name');
          }
          if (driver.phone && currentDriver.phone !== driver.phone) {
            changes['Phone'] = { before: currentDriver.phone, after: driver.phone };
            changedFields.push('phone');
          }
          if (driver.status && currentDriver.status !== driver.status) {
            changes['Status'] = { before: currentDriver.status, after: driver.status };
            changedFields.push('status');
          }
          if (driver.licenseNumber && currentDriver.licenseNumber !== driver.licenseNumber) {
            changes['License Number'] = { before: currentDriver.licenseNumber, after: driver.licenseNumber };
            changedFields.push('license number');
          }
          if (driver.licenseExpiry && currentDriver.licenseExpiry !== driver.licenseExpiry) {
            changes['License Expiry'] = { before: currentDriver.licenseExpiry, after: driver.licenseExpiry };
            changedFields.push('license expiry');
          }
          if (driver.nationality && currentDriver.nationality !== driver.nationality) {
            changes['Nationality'] = { before: currentDriver.nationality, after: driver.nationality };
            changedFields.push('nationality');
          }
        }
        
        if (Object.keys(changes).length > 0) {
          const driverName = driver.name || currentDriver?.name || `Driver ID: ${id}`;
          await logActivity({
            entity_type: 'driver',
            entity_id: id,
            action_type: 'update',
            changes,
            description: `Updated driver: ${driverName} (changed: ${changedFields.join(', ')})`,
          });
          console.log('✅ Driver update logged to activity log');
        }
      } catch (logError) {
        console.error('⚠️ Failed to log driver update:', logError);
        // Don't fail the update if logging fails
      }
      
      // Convert from database schema to app schema
      const formattedDriver: Driver = {
        id: updatedData.id,
        name: updatedData.name,
        phone: updatedData.phone,
        licenseNumber: updatedData.license_number,
        licenseExpiry: updatedData.license_expiry,
        image_url: updatedData.image_url,
        status: updatedData.status as any,
        currentVehicleId: updatedData.current_vehicle_id,
        dailyTarget: updatedData.daily_target,
        deposit: updatedData.deposit,
        nationality: updatedData.nationality,
        notes: updatedData.notes,
        created_at: updatedData.created_at,
      };
      
      // Update Zustand store with new data
      set({ 
        drivers: get().drivers.map(d => d.id === id ? formattedDriver : d),
        selectedDriver: formattedDriver,
        loading: false 
      });
      
      // Invalidate cache to force refresh on next fetch
      try {
        await driverHybridStore.refreshFromServer();
      } catch (cacheError) {
        console.warn('⚠️ Cache refresh failed (non-critical):', cacheError);
      }
    } catch (error: any) {
      console.error('❌ Error updating driver (hybrid):', error);
      set({ loading: false, error: error.message });
    }
  },
  
  removeDriver: async (id) => {
    set({ loading: true, error: null });
    try {
      // Delete directly from database (bypass hybrid storage for immediate effect)
      await deleteDriver(id);
      console.log('✅ Driver deleted from database:', id);
      
      // Update local state immediately
      set({ 
        drivers: get().drivers.filter(d => d.id !== id),
        selectedDriver: get().selectedDriver?.id === id ? null : get().selectedDriver,
        loading: false 
      });
      
      // Refresh cache in background to stay in sync
      setTimeout(() => {
        driverHybridStore.refreshFromServer().catch(err => 
          console.warn('⚠️ Background cache refresh failed:', err)
        );
      }, 100);
    } catch (error: any) {
      console.error('❌ Error deleting driver:', error);
      set({ loading: false, error: error.message });
      throw error; // Re-throw so UI can show error
    }
  },
  
  setSelectedDriver: (driver) => {
    set({ selectedDriver: driver });
  },
  
  assignDriver: async (driverId, vehicleId) => {
    set({ loading: true, error: null });
    try {
      // Update driver status using hybrid storage (offline-first)
      await driverHybridStore.update(driverId, { 
        status: 'active',
        assigned_vehicle_id: vehicleId 
      });
      
      console.log('✅ Driver assigned (hybrid):', { driverId, vehicleId });
      
      // Update local state
      set({ 
        drivers: get().drivers.map(d => 
          d.id === driverId 
            ? { ...d, status: 'active', currentVehicleId: vehicleId }
            : d
        ),
        loading: false 
      });
    } catch (error: any) {
      console.error('❌ Error assigning driver (hybrid):', error);
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  unassignDriver: async (driverId) => {
    set({ loading: true, error: null });
    try {
      // Update driver using hybrid storage (offline-first)
      // NOTE: Driver remains 'active' - just removes vehicle assignment
      await driverHybridStore.update(driverId, { 
        assigned_vehicle_id: undefined 
      });
      
      console.log('✅ Driver unassigned (hybrid) - remains active:', driverId);
      
      // Update local state - driver stays active, just removes vehicle
      set({ 
        drivers: get().drivers.map(d => 
          d.id === driverId 
            ? { ...d, currentVehicleId: undefined }
            : d
        ),
        loading: false 
      });
    } catch (error: any) {
      console.error('❌ Error unassigning driver (hybrid):', error);
      set({ loading: false, error: error.message });
      throw error;
    }
  },
}));
